import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, CalendarIcon, ZapIcon, TrendingUpIcon, BarChart3Icon, UsersIcon, MessageSquareIcon, PhoneIcon, GlobeIcon, CheckCircleIcon, SearchIcon } from 'lucide-react';
import { ScrollReveal } from '../../components/ScrollReveal';
import { SEOHead } from '../../components/SEOHead';
const faqs = [{
  question: 'How did Viridia help TFP Landscaping get more leads?',
  answer: 'We implemented our Business Essentials package, which includes GEO-tuned Yelp Ads and an AI-powered Request a Quote (RAQ) system. By responding instantly to inquiries instead of waiting 4+ hours, TFP was able to capture leads before competitors could.'
}, {
  question: 'What results did TFP Landscaping see in 30 days?',
  answer: 'In just 30 days, TFP Landscaping saw a 284% increase in leads, totaling 242 Yelp leads. They achieved a 39.95% close rate on those leads, with 28 estimates booked end-to-end entirely by the AI system.'
}];
export function TfpLandscapingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-838">
      <SEOHead title="TFP Landscaping Case Study | 284% More Leads | Viridia Analytics" description="See how TFP Landscaping used Viridia Analytics' Business Essentials package to get 242 Yelp leads, 28 AI-booked estimates, and a 39.95% close rate in 30 days." canonical="/case-studies/tfp-landscaping" keywords="landscaping marketing case study, Yelp ads for landscapers, AI lead conversion, TFP Landscaping" faq={faqs} data-id="element-839" />

      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-840">
        <ScrollReveal data-id="element-841">
          <div className="mb-6" data-id="element-842">
            <Link to="/case-studies" className="inline-flex items-center text-gray-400 hover:text-white transition-colors text-sm font-medium" data-id="element-843">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-844" />
              Back to Case Studies
            </Link>
          </div>

          <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl" data-id="element-845">
            {/* Header / Snapshot */}
            <div className="bg-gray-900 p-8 lg:p-12 relative overflow-hidden border-b border-gray-800" data-id="element-846">
              <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]" data-id="element-847"></div>
              <div className="relative z-10" data-id="element-848">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-849">
                  Landscaping Success Story
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-4xl" data-id="element-850">
                  TFP Landscaping: 284% More Leads and Instant Responses With
                  Viridia's Business Essentials Package
                </h1>

                {/* Snapshot Pills */}
                <div className="flex flex-wrap gap-3 mb-10" data-id="element-851">
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-852">
                    <MapPinIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-853" />
                    Phoenix Landscaper (18 yrs in business)
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-854">
                    <ZapIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-855" />
                    Business Essentials Package
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-856">
                    <CalendarIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-857" />
                    Client since July 2025
                  </div>
                </div>

                {/* Hero Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-858">
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-859">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-860">
                      284%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-861">
                      Lead Increase
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-862">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-863">
                      242
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-864">
                      Total Leads
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-865">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-866">
                      39.95%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-867">Close Rate</p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-868">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-869">
                      28
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-870">
                      AI-Booked Estimates
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Challenge / Solution */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-871">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-872">
                <div data-id="element-873">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-6" data-id="element-874">
                    <TrendingUpIcon className="w-6 h-6 text-red-500" data-id="element-875" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-876">
                    The Challenge
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-877">
                    TFP Landscaping is a Phoenix-based operation with 18 years
                    in business. With one owner running multiple crews and no
                    office staff, their average response time was 4+ hours. Yelp
                    ad spend was being wasted because leads went cold before
                    anyone could reply.
                  </p>
                </div>
                <div data-id="element-878">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-879">
                    <BarChart3Icon className="w-6 h-6 text-blue-500" data-id="element-880" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-881">
                    What Viridia Implemented
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-882">
                    We deployed the Business Essentials package - Yelp Ads
                    management with GEO tuning, an AI-powered Request a Quote
                    (RAQ) system for instant response, and automated lead
                    handling so every inquiry gets answered in seconds, even
                    outside business hours.
                  </p>
                </div>
              </div>
            </div>

            {/* 30-Day Performance Breakdown */}
            <div className="p-8 lg:p-12 bg-viridia-surface border-b border-gray-800" data-id="element-883">
              <h3 className="text-2xl font-bold text-white mb-8" data-id="element-884">
                30-Day Metrics
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-885">
                <div className="space-y-6" data-id="element-886">
                  <div className="bg-viridia-black p-6 rounded-xl border border-gray-800" data-id="element-887">
                    <div className="flex justify-between items-center mb-2" data-id="element-888">
                      <span className="text-gray-400" data-id="element-889">Impressions</span>
                      <span className="text-white font-bold" data-id="element-890">36.4k</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-891">
                      <div className="bg-blue-500 h-2 rounded-full" style={{
                      width: '70%'
                    }} data-id="element-892"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-893">
                      25.5k (70%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-black p-6 rounded-xl border border-gray-800" data-id="element-894">
                    <div className="flex justify-between items-center mb-2" data-id="element-895">
                      <span className="text-gray-400" data-id="element-896">Page Visits</span>
                      <span className="text-white font-bold" data-id="element-897">376</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-898">
                      <div className="bg-blue-500 h-2 rounded-full" style={{
                      width: '77%'
                    }} data-id="element-899"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-900">
                      288 (77%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-black p-6 rounded-xl border border-gray-800" data-id="element-901">
                    <div className="flex justify-between items-center mb-2" data-id="element-902">
                      <span className="text-gray-400" data-id="element-903">Total Leads</span>
                      <span className="text-white font-bold" data-id="element-904">242</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-905">
                      <div className="bg-blue-500 h-2 rounded-full" style={{
                      width: '74%'
                    }} data-id="element-906"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-907">
                      179 (74%) from Yelp Ads
                    </p>
                  </div>
                </div>

                <div data-id="element-908">
                  <h4 className="text-lg font-semibold text-white mb-4" data-id="element-909">
                    Lead Breakdown
                  </h4>
                  <div className="bg-viridia-black rounded-xl border border-gray-800 overflow-hidden" data-id="element-910">
                    <table className="w-full text-left text-sm" data-id="element-911">
                      <thead className="bg-gray-900 border-b border-gray-800" data-id="element-912">
                        <tr data-id="element-913">
                          <th className="p-4 text-gray-400 font-medium" data-id="element-914">
                            Lead Type
                          </th>
                          <th className="p-4 text-gray-400 font-medium" data-id="element-915">
                            Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800" data-id="element-916">
                        <tr data-id="element-917">
                          <td className="p-4 text-white flex items-center" data-id="element-918">
                            <MessageSquareIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-919" />{' '}
                            Messages
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-920">123</td>
                        </tr>
                        <tr data-id="element-921">
                          <td className="p-4 text-white flex items-center" data-id="element-922">
                            <PhoneIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-923" />{' '}
                            Calls
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-924">53</td>
                        </tr>
                        <tr data-id="element-925">
                          <td className="p-4 text-white flex items-center" data-id="element-926">
                            <GlobeIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-927" />{' '}
                            Website Visits
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-928">41</td>
                        </tr>
                        <tr data-id="element-929">
                          <td className="p-4 text-white flex items-center" data-id="element-930">
                            <CheckCircleIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-931" />{' '}
                            Other Leads
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-932">25</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Bookings & Close Rate */}
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-933">
              <div className="p-8 lg:p-12 bg-viridia-black border-r border-gray-800" data-id="element-934">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-935">
                  <ZapIcon className="w-6 h-6 text-blue-500" data-id="element-936" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-937">
                  AI-Booked Estimates
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-938">
                  28 estimates were booked end-to-end by AI directly inside Yelp
                  RAQ. By replacing a 4+ hour lag with an instant response, the
                  AI captured homeowners while they were still actively looking
                  to hire.
                </p>
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-viridia-surface border border-gray-800 text-white font-medium" data-id="element-939">
                  Response Time:{' '}
                  <span className="text-blue-500 ml-2" data-id="element-940">
                    From 4+ hours to Instant
                  </span>
                </div>
              </div>
              <div className="p-8 lg:p-12 bg-viridia-black" data-id="element-941">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-942">
                  <UsersIcon className="w-6 h-6 text-blue-500" data-id="element-943" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-944">
                  Close Rate
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-945">
                  Because leads were engaged instantly and qualified
                  automatically, TFP Landscaping reported a 39.95% close rate on
                  the estimates that were booked.
                </p>
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-viridia-surface border border-gray-800 text-white font-medium" data-id="element-946">
                  Client-Reported Close Rate:{' '}
                  <span className="text-blue-500 ml-2" data-id="element-947">39.95%</span>
                </div>
              </div>
            </div>

            {/* Owning the Local Map & 12-Month Impact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-gray-800" data-id="element-948">
              <div className="p-8 lg:p-12 bg-viridia-surface border-r border-gray-800" data-id="element-949">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-950">
                  <MapPinIcon className="w-6 h-6 text-blue-500" data-id="element-951" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-952">
                  GEO & Local Visibility
                </h3>
                <ul className="space-y-4 mb-8" data-id="element-953">
                  <li className="flex items-start" data-id="element-954">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-955" />
                    <span className="text-gray-300" data-id="element-956">
                      TFP earned more page views than similar landscaping
                      businesses.
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-957">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-958" />
                    <span className="text-gray-300" data-id="element-959">
                      GEO-tuned campaigns targeted specific high-value
                      neighborhoods.
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-960">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-961" />
                    <span className="text-gray-300" data-id="element-962">
                      Successfully competing with high-volume brands like Moon
                      Valley Nurseries.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-963">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-964">
                  <SearchIcon className="w-6 h-6 text-blue-500" data-id="element-965" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-966">
                  12-Month Impact
                </h3>
                <p className="text-gray-300 mb-6" data-id="element-967">
                  Over the last 12 months, Yelp Ads have generated an additional{' '}
                  <strong className="text-white" data-id="element-968">582 leads</strong> for TFP
                  Landscaping.
                </p>
                <div data-id="element-969">
                  <p className="text-sm text-gray-400 mb-3 uppercase tracking-wider font-semibold" data-id="element-970">
                    Trending Searches Captured:
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-971">
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-972">
                      Fountain Repair{' '}
                      <span className="text-blue-500 ml-1" data-id="element-973">+87%</span>
                    </span>
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-974">
                      Turf Installation{' '}
                      <span className="text-blue-500 ml-1" data-id="element-975">+50%</span>
                    </span>
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-976">
                      Irrigation{' '}
                      <span className="text-blue-500 ml-1" data-id="element-977">+50%</span>
                    </span>
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-978">
                      Landscaping{' '}
                      <span className="text-blue-500 ml-1" data-id="element-979">+40%</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial */}
            <div className="p-8 lg:p-12 bg-viridia-black border-t border-gray-800 text-center relative" data-id="element-980">
              <div className="text-blue-500 text-6xl absolute top-4 left-1/2 -translate-x-1/2 opacity-10 font-serif leading-none" data-id="element-981">
                "
              </div>
              <p className="text-xl md:text-2xl text-white italic relative z-10 max-w-3xl mx-auto mb-6 leading-relaxed" data-id="element-982">
                I'm getting more jobs with the same ad spend. The auto response
                system is booking me almost 10 new estimates per week, and
                almost half of them become clients.
              </p>
              <p className="text-blue-500 font-bold uppercase tracking-wider text-sm" data-id="element-983">
                Owner, TFP Landscaping - Phoenix, AZ
              </p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2} data-id="element-984">
          <div className="mt-12 text-center" data-id="element-985">
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-986">
              Want to see what's leaking leads in your pipeline?
            </h3>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-987">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-988" />
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>;
}